import { type FC, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { useLanguagesQuery } from "@/partner/entities/languages";

interface LanguageOption {
  value: string;
  label: string;
}

interface LanguageFieldProps {
  label: string;
  value: string[];
  fullWidth?: boolean;
  onChange: (languageCodes: string[]) => void;
}

/**
 * Мультиселект языков — переиспользует Query.languages (тот же запрос, что
 * и LanguageAutocomplete в entities/languages, используемый для выбора
 * Platform.languageId), а не заводит статический список заново. Живёт в
 * features/users (а не partner/shared/ui, как для остальных статических
 * filter-полей Task 19, и не entities/languages), потому что зависит от
 * entities-слоя (useLanguagesQuery) — boundaries/element-types запрещает
 * shared импортировать entities, а Filter (shared) уже переиспользует этот
 * приём для CreatorsAutocomplete (см. features/users/ui/CreatorsAutocomplete.tsx),
 * так что LanguageField следует тому же паттерну для консистентности.
 *
 * В отличие от LanguageAutocomplete (одиночный выбор по Language.id, для
 * Platform.languageId), значение здесь — Language.code (например "en"/"ru"):
 * именно код языка, а не id, хранится в User.language и фильтруется в
 * AnalyticsUsersFilterInput.languages: [String!].
 */
export const LanguageField: FC<LanguageFieldProps> = ({
  label,
  value,
  onChange,
}) => {
  const [inputValue, setInputValue] = useState("");
  const debouncedSearch = useDebounce(inputValue, 300);

  const { data, loading } = useLanguagesQuery({
    variables: { search: debouncedSearch || undefined },
    fetchPolicy: "cache-and-network",
  });

  const options: LanguageOption[] = (data?.languages ?? []).map((language) => ({
    value: language.code,
    label: language.code
      ? `${language.title} (${language.code})`
      : language.title,
  }));

  return (
    <Autocomplete<LanguageOption, true>
      multiple
      fullWidth
      filterOptions={(opts) => opts}
      loading={loading}
      options={options}
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) =>
        option.value === selected.value
      }
      value={options.filter((option) => value.includes(option.value))}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue, reason) => {
        if (reason !== "reset") {
          setInputValue(newInputValue);
        }
      }}
      onChange={(_event, newValue) =>
        onChange(newValue.map((option) => option.value))
      }
      renderInput={(params) => <TextField {...params} label={label} />}
    />
  );
};
